import React, { useEffect, useState } from "react";
import { apiClient } from "@/api/apiClient";
import { GeoData } from "@/types/GeoData";
import { Switch } from "@/components/ui/switch";
import { Card, CardHeader, CardContent  } from "@/components/ui/card";
import { Loader2 } from "lucide-react";
import GeoMap from "@/components/GeoMap/GeoMap";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Route } from "@/types/routes";
import {Device} from "@/types/devices";

const PUBLIC_DEVICE_TYPE = ["public"];



const PublicMap: React.FC = () => {
    const [newPoint, setNewPoint] = useState<GeoData | null>(null);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [ws, setWs] = useState<WebSocket | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [routes, setRoutes] = useState<Route[]>([]);
    const [routesMap, setRoutesMap] = useState<Record<string, Route>>({});
    const [devicesMap, setDevicesMap] = useState<Record<string, Device>>({});
    const [enabledRoutes, setEnabledRoutes] = useState<Record<string, boolean>>({});


    const toggleRoute = (routeId: string) => {
        setEnabledRoutes((prev) => ({
            ...prev,
            [routeId]: !prev[routeId],
        }));
    };


    useEffect(() => {
        setLoading(true);
        apiClient.getLastDevicePoints(PUBLIC_DEVICE_TYPE)
            .then((points) => {
                points.forEach((point, index) => {
                    setTimeout(() => setNewPoint(point), index * 100);
                });
            })
            .catch((error) => console.error("Ошибка загрузки начальных точек:", error))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        apiClient.getActiveRoutes()
            .then((activeRoutes) => {
                const map: Record<string, Route> = {};
                activeRoutes.forEach((route) => {
                    map[route.id] = route;
                });
                setRoutesMap(map);
            })
            .catch((error) => console.error("Ошибка загрузки маршрутов:", error));
    }, []);

    useEffect(() => {
        let newWs: WebSocket | null = null;

        (async () => {
            newWs = await apiClient.connectRealtime(
                PUBLIC_DEVICE_TYPE,
                [],
                (point: GeoData) => setNewPoint(point),
                (error: string) => console.error(`Ошибка WebSocket: ${error}`)
            );
            setWs(newWs);
        })();

        return () => {
            newWs?.close();
        };
    }, []);

    useEffect(() => {
        apiClient.getDevices()
            .then((devices) => {
                const publicDevices = devices.filter((device) => PUBLIC_DEVICE_TYPE.includes(device.typeId));
                const map: Record<string, Device> = {};
                publicDevices.forEach((device) => {
                    map[device.id] = device;
                });
                setDevicesMap(map);
            })
            .catch((error) => console.error("Ошибка загрузки устройств:", error));
    }, []);

    useEffect(() => {
        if (newPoint) {
            const device = devicesMap[newPoint.device];
            if (device && device.route_id) {
                const route = routesMap[device.route_id];
                if (route) {
                    setRoutes((prevRoutes) => {
                        if (prevRoutes.find((r) => r.id === route.id)) return prevRoutes;
                        return [...prevRoutes, route];
                    });
                    setEnabledRoutes((prev) => ({
                        ...prev,
                        [route.id]: true,
                    }));
                }
            }
        }
    }, [newPoint, devicesMap, routesMap, routes]);

    return (
        <div  className="p-6 space-y-4">
            {routes.length > 0 && (
                <Card>
                    <CardHeader className="font-semibold">Маршруты</CardHeader>
                    <CardContent className="flex flex-wrap gap-4">
                        {routes.map((route) => (
                            <div key={route.id} className="flex items-center space-x-2">
                                <Switch
                                    checked={enabledRoutes[route.id] || false}
                                    onCheckedChange={() => toggleRoute(route.id)}
                                />
                                <span className="text-sm font-medium">{route.name}</span>
                            </div>
                        ))}
                    </CardContent>
                </Card>
            )}
            <div className="w-full h-full">
                {loading ? (
                    <div className="flex justify-center items-center">
                        <Loader2 className="animate-spin w-8 h-8 text-primary" />
                    </div>
                ) : (
                    <Card className="mw-full h-full overflow-hidden">
                        <TooltipProvider>
                            <GeoMap deviceTypes={PUBLIC_DEVICE_TYPE} newPoint={newPoint} routes={routes.filter((route) => enabledRoutes[route.id])} />
                        </TooltipProvider>
                    </Card>
                )}
            </div>
        </div>
    );
};

export default PublicMap;
